import {
  createContext,
  createElement,
  useContext,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { useContent } from './ContentContext';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */
export type TypographyOverride = {
  fontFamily?: string; // font key from FONTS
  fontSize?: number; // px
  fontWeight?: number;
  color?: string; // hex
  lineHeight?: number; // unitless
  letterSpacing?: number; // em
  textAlign?: 'left' | 'center' | 'right';
  italic?: boolean;
};

export type DesignData = {
  typography: Record<string, TypographyOverride>;
  order: { sections?: string[] };
  hidden: Record<string, boolean>;
};

export type DesignContextValue = {
  design: DesignData;
  editMode: boolean;
  selectedId: string | null;
  selectedLabel: string | null;
  select: (id: string | null, label?: string) => void;
  setTypography: (id: string, patch: Partial<TypographyOverride>) => void;
  resetTypography: (id: string) => void;
  moveSectionBefore: (fromKey: string, toKey: string) => void;
  toggleHidden: (key: string) => void;
  moveListItem: (path: (string | number)[], from: number, to: number) => void;
};

/* ------------------------------------------------------------------ */
/* Curated font list (Google Fonts)                                    */
/* ------------------------------------------------------------------ */
export type FontDef = {
  key: string;
  label: string;
  family: string;
  stack: string;
  cat: 'Serif' | 'Sans' | 'Schreibschrift';
  weights: number[];
};

export const FONTS: FontDef[] = [
  { key: 'playfair', label: 'Playfair Display', family: 'Playfair Display', stack: "'Playfair Display', serif", cat: 'Serif', weights: [400, 500, 600, 700, 800] },
  { key: 'lora', label: 'Lora', family: 'Lora', stack: "'Lora', serif", cat: 'Serif', weights: [400, 500, 600, 700] },
  { key: 'cormorant', label: 'Cormorant Garamond', family: 'Cormorant Garamond', stack: "'Cormorant Garamond', serif", cat: 'Serif', weights: [300, 400, 500, 600, 700] },
  { key: 'ebgaramond', label: 'EB Garamond', family: 'EB Garamond', stack: "'EB Garamond', serif", cat: 'Serif', weights: [400, 500, 600, 700, 800] },
  { key: 'merriweather', label: 'Merriweather', family: 'Merriweather', stack: "'Merriweather', serif", cat: 'Serif', weights: [300, 400, 700, 900] },
  { key: 'montserrat', label: 'Montserrat', family: 'Montserrat', stack: "'Montserrat', sans-serif", cat: 'Sans', weights: [300, 400, 500, 600, 700, 800] },
  { key: 'poppins', label: 'Poppins', family: 'Poppins', stack: "'Poppins', sans-serif", cat: 'Sans', weights: [300, 400, 500, 600, 700, 800] },
  { key: 'raleway', label: 'Raleway', family: 'Raleway', stack: "'Raleway', sans-serif", cat: 'Sans', weights: [300, 400, 500, 600, 700, 800] },
  { key: 'lato', label: 'Lato', family: 'Lato', stack: "'Lato', sans-serif", cat: 'Sans', weights: [300, 400, 700, 900] },
  { key: 'opensans', label: 'Open Sans', family: 'Open Sans', stack: "'Open Sans', sans-serif", cat: 'Sans', weights: [300, 400, 500, 600, 700, 800] },
  { key: 'nunito', label: 'Nunito', family: 'Nunito', stack: "'Nunito', sans-serif", cat: 'Sans', weights: [300, 400, 500, 600, 700, 800] },
  { key: 'inter', label: 'Inter', family: 'Inter', stack: "'Inter', sans-serif", cat: 'Sans', weights: [300, 400, 500, 600, 700] },
  { key: 'greatvibes', label: 'Great Vibes', family: 'Great Vibes', stack: "'Great Vibes', cursive", cat: 'Schreibschrift', weights: [400] },
  { key: 'dancing', label: 'Dancing Script', family: 'Dancing Script', stack: "'Dancing Script', cursive", cat: 'Schreibschrift', weights: [400, 500, 600, 700] },
  { key: 'parisienne', label: 'Parisienne', family: 'Parisienne', stack: "'Parisienne', cursive", cat: 'Schreibschrift', weights: [400] },
  { key: 'sacramento', label: 'Sacramento', family: 'Sacramento', stack: "'Sacramento', cursive", cat: 'Schreibschrift', weights: [400] },
];

const FONT_BY_KEY: Record<string, FontDef> = Object.fromEntries(FONTS.map((f) => [f.key, f]));

export function fontDef(key?: string): FontDef | undefined {
  return key ? FONT_BY_KEY[key] : undefined;
}

export const WEIGHT_LABELS: Record<number, string> = {
  300: 'Dünn', 400: 'Normal', 500: 'Medium', 600: 'Halbfett', 700: 'Fett', 800: 'Extrafett', 900: 'Schwarz',
};

export function fontStack(key?: string): string | undefined {
  if (!key) return undefined;
  return FONT_BY_KEY[key]?.stack;
}

const loadedFonts = new Set<string>();

export function ensureFontLoaded(key?: string) {
  if (!key || loadedFonts.has(key) || typeof document === 'undefined') return;
  const def = FONT_BY_KEY[key];
  if (!def) return;
  loadedFonts.add(key);
  const href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(def.family)}:wght@${def.weights.join(';')}&display=swap`;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  link.dataset.eefont = key;
  document.head.appendChild(link);
}

/* ------------------------------------------------------------------ */
/* Style building                                                      */
/* ------------------------------------------------------------------ */
export function buildStyle(ov?: TypographyOverride): CSSProperties | undefined {
  if (!ov) return undefined;
  const s: CSSProperties = {};
  if (ov.fontFamily) {
    ensureFontLoaded(ov.fontFamily);
    s.fontFamily = fontStack(ov.fontFamily);
  }
  if (ov.fontSize) s.fontSize = `${ov.fontSize}px`;
  if (ov.fontWeight) s.fontWeight = ov.fontWeight;
  if (ov.color) s.color = ov.color;
  if (ov.lineHeight) s.lineHeight = ov.lineHeight;
  if (typeof ov.letterSpacing === 'number') s.letterSpacing = `${ov.letterSpacing}em`;
  if (ov.textAlign) s.textAlign = ov.textAlign;
  if (ov.italic) s.fontStyle = 'italic';
  return s;
}

/* ------------------------------------------------------------------ */
/* Normalisation                                                       */
/* ------------------------------------------------------------------ */
export function normalizeDesign(raw: any): DesignData {
  const d = raw && typeof raw === 'object' ? raw : {};
  return {
    typography: d.typography && typeof d.typography === 'object' ? d.typography : {},
    order: d.order && typeof d.order === 'object' ? d.order : {},
    hidden: d.hidden && typeof d.hidden === 'object' ? d.hidden : {},
  };
}

/* Canonical order of the movable content sections. */
export const DEFAULT_SECTION_ORDER = ['about', 'features', 'target', 'details', 'cta'];

export function resolveSectionOrder(design: DesignData): string[] {
  const saved = Array.isArray(design.order?.sections) ? design.order.sections : [];
  const known = new Set(DEFAULT_SECTION_ORDER);
  const ordered = saved.filter((k) => known.has(k));
  for (const k of DEFAULT_SECTION_ORDER) if (!ordered.includes(k)) ordered.push(k);
  return ordered;
}

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */
const NOOP_DESIGN: DesignData = { typography: {}, order: {}, hidden: {} };

const DesignContext = createContext<DesignContextValue>({
  design: NOOP_DESIGN,
  editMode: false,
  selectedId: null,
  selectedLabel: null,
  select: () => {},
  setTypography: () => {},
  resetTypography: () => {},
  moveSectionBefore: () => {},
  toggleHidden: () => {},
  moveListItem: () => {},
});

export function useDesign(): DesignContextValue {
  return useContext(DesignContext);
}

export { DesignContext };

/** Public (read-only) provider: applies saved design to the live website. */
export function PublicDesignProvider({ children }: { children: ReactNode }) {
  const content = useContent() as any;
  const design = normalizeDesign(content?.design);
  for (const ov of Object.values(design.typography)) ensureFontLoaded((ov as TypographyOverride).fontFamily);
  const value: DesignContextValue = {
    design,
    editMode: false,
    selectedId: null,
    selectedLabel: null,
    select: () => {},
    setTypography: () => {},
    resetTypography: () => {},
    moveSectionBefore: () => {},
    toggleHidden: () => {},
    moveListItem: () => {},
  };
  return <DesignContext.Provider value={value}>{children}</DesignContext.Provider>;
}

/* ------------------------------------------------------------------ */
/* <Editable> – a text element whose typography can be edited          */
/* ------------------------------------------------------------------ */
type EditableProps = {
  id: string;
  as?: any;
  label?: string;
  html?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

export function Editable({ id, as = 'div', label, html, className = '', style, children }: EditableProps) {
  const ctx = useDesign();
  const ov = ctx.design.typography[id];
  const ovStyle = buildStyle(ov);
  const selected = ctx.editMode && ctx.selectedId === id;
  const cls = [className, ctx.editMode ? 'ee-ed' : '', selected ? 'ee-ed-sel' : ''].filter(Boolean).join(' ');

  const props: any = {
    className: cls,
    style: ovStyle || style ? { ...style, ...ovStyle } : undefined,
    'data-eid': id,
  };
  if (ctx.editMode) {
    props.title = label || id;
    props.onClickCapture = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      ctx.select(id, label);
    };
  }
  if (html != null) {
    props.dangerouslySetInnerHTML = { __html: html };
    return createElement(as, props);
  }
  return createElement(as, props, children);
}

/* ------------------------------------------------------------------ */
/* <Sortable> – a list entry that can be dragged to reorder            */
/* ------------------------------------------------------------------ */
type SortableProps = {
  path: (string | number)[];
  index: number;
  className?: string;
  children?: ReactNode;
};

export function Sortable({ path, index, className = '', children }: SortableProps) {
  const ctx = useDesign();
  const [over, setOver] = useState(false);
  const key = path.join('.');

  if (!ctx.editMode) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      className={`ee-sortable ${over ? 'ee-sortable-over' : ''} ${className}`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('application/ee-list')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const raw = e.dataTransfer.getData('application/ee-list');
        if (!raw) return;
        try {
          const data = JSON.parse(raw);
          if (data.key === key && typeof data.index === 'number' && data.index !== index) {
            e.preventDefault();
            ctx.moveListItem(path, data.index, index);
          }
        } catch { /* ignore */ }
      }}
    >
      <button
        type="button"
        className="ee-drag-handle"
        title="Zum Verschieben ziehen"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('application/ee-list', JSON.stringify({ key, index }));
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <i className="ri-draggable" />
      </button>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* <SectionShell> – wraps a whole page section (drag to reorder / hide) */
/* ------------------------------------------------------------------ */
type SectionShellProps = {
  sectionKey: string;
  label: string;
  children: ReactNode;
};

export function SectionShell({ sectionKey, label, children }: SectionShellProps) {
  const ctx = useDesign();
  const [over, setOver] = useState(false);
  const hidden = !!ctx.design.hidden[sectionKey];

  if (!ctx.editMode) {
    if (hidden) return null;
    return <>{children}</>;
  }

  return (
    <div
      className={`ee-section-shell ${over ? 'ee-section-over' : ''} ${hidden ? 'ee-section-hidden' : ''}`}
      data-section={sectionKey}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('application/ee-section')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false);
        const from = e.dataTransfer.getData('application/ee-section');
        if (from && from !== sectionKey) {
          e.preventDefault();
          ctx.moveSectionBefore(from, sectionKey);
        }
      }}
    >
      <div className="ee-section-bar">
        <button
          type="button"
          className="ee-section-drag"
          title={`„${label}" verschieben`}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('application/ee-section', sectionKey);
          }}
        >
          <i className="ri-drag-move-2-line" />
        </button>
        <button
          type="button"
          title={hidden ? `„${label}" einblenden` : `„${label}" ausblenden`}
          onClick={() => ctx.toggleHidden(sectionKey)}
        >
          <i className={hidden ? 'ri-eye-off-line' : 'ri-eye-line'} />
        </button>
      </div>
      {children}
    </div>
  );
}
